import React from 'react';
import { GrcButton, GrcButtonType } from '@grc/ui-package';
import { useI18nState } from '../stores/settings/language-context';
import type { SignInErrorText } from '../models/auth0/sign-in-error-text.interface';

// Exactly one way on: "Try again", or (access_denied) only a logout back to the login page.
type AuthErrorProps = {
  errorText?: SignInErrorText;
  serviceMessage?: string;
} & (
  | { onRetry: () => void; onBackToLogin?: never }
  | { onBackToLogin: () => void; onRetry?: never }
);

// Sign-in could not be completed: an Auth0 error, shown as our own text (errorText), or, with no
// errorText, the loop guard stopped the automatic login redirects. serviceMessage is the sign-in
// service's own message, which a crafted link can set, so it is shown only below ours and marked
// as coming from that service.
const AuthError = ({
  errorText,
  serviceMessage,
  onRetry,
  onBackToLogin
}: AuthErrorProps) => {
  const { localeData } = useI18nState();

  return (
    <div
      data-testid="auth_error"
      className="auth-error-screen d-flex flex-column justify-content-center align-items-center vh-100"
    >
      <h4>{localeData.SignInFailed}</h4>
      <p>{errorText ? localeData[errorText] : localeData.SignInRedirectLoop}</p>
      {serviceMessage && (
        <p
          data-testid="auth_error_service_message"
          className="grc-text-light text-sx"
        >
          {localeData.SignInServiceMessage} {serviceMessage}
        </p>
      )}
      {onBackToLogin ? (
        <GrcButton
          id="auth_error_back_to_login"
          text={localeData.BackToLogin}
          className="grc-btn-primary"
          type={GrcButtonType.Normal}
          onClick={onBackToLogin}
        />
      ) : (
        <GrcButton
          id="auth_error_try_again"
          text={localeData.TryAgain}
          className="grc-btn-primary"
          type={GrcButtonType.Normal}
          onClick={onRetry}
        />
      )}
    </div>
  );
};

export default AuthError;
